import { MenuItem, Stack, TextField } from "@mui/material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import type { SlaDurationUnit } from "model/slaModel";
import {
  SLA_DURATION_UNITS,
  SLA_DURATION_UNIT_LABEL_KEYS,
  SLA_EDITOR_TEXT_KEYS,
  type SlaTargetDraft,
} from "model/slaEditorModel";

/**
 * Form durata relativa dell'editor SLA (piano Client sez. 3 e sez. 5 F2a):
 * componente riusabile, senza deadline assoluta. Il modello lavorativo e la
 * conversione in secondi restano del Server: qui si scelgono solo valore e
 * unita', che sono gli unici campi di `duration_*` accettati a design-time.
 */
interface SlaDurationFieldsProps {
  draft: SlaTargetDraft;
  onChange: (patch: Partial<SlaTargetDraft>) => void;
  /** Chiavi di traduzione degli errori dei due campi, gia' filtrate dal chiamante. */
  durationErrorKey?: string;
  unitErrorKey?: string;
  disabled?: boolean;
  /** Prefisso degli identificatori DOM: la superficie puo' avere piu' card. */
  idPrefix: string;
}

const SlaDurationFields = ({
  draft,
  onChange,
  durationErrorKey,
  unitErrorKey,
  disabled = false,
  idPrefix,
}: SlaDurationFieldsProps) => {
  const translations = useTranslations();

  const onChangeValue = (raw: string) => {
    const parsed = raw === "" ? null : Number(raw);
    onChange({
      durationValue:
        parsed === null || !Number.isFinite(parsed) ? null : parsed,
    });
  };

  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "flex-start" }}>
      <TextField
        id={`${idPrefix}-duration-value`}
        size="small"
        type="number"
        fullWidth
        disabled={disabled}
        label={getTranslation(translations, SLA_EDITOR_TEXT_KEYS.durationValue)}
        inputProps={{ min: 0, step: "any" }}
        value={draft.durationValue ?? ""}
        error={Boolean(durationErrorKey)}
        helperText={
          durationErrorKey
            ? getTranslation(translations, durationErrorKey)
            : undefined
        }
        onChange={(event) => onChangeValue(event.target.value)}
      />
      <TextField
        id={`${idPrefix}-duration-unit`}
        select
        size="small"
        fullWidth
        disabled={disabled}
        label={getTranslation(translations, SLA_EDITOR_TEXT_KEYS.durationUnit)}
        value={draft.durationUnit ?? ""}
        error={Boolean(unitErrorKey)}
        helperText={
          unitErrorKey ? getTranslation(translations, unitErrorKey) : undefined
        }
        onChange={(event) =>
          onChange({
            durationUnit:
              event.target.value === ""
                ? null
                : (Number(event.target.value) as SlaDurationUnit),
          })
        }
      >
        {SLA_DURATION_UNITS.map((unit) => (
          <MenuItem key={unit} value={unit}>
            {getTranslation(translations, SLA_DURATION_UNIT_LABEL_KEYS[unit])}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
};

export default SlaDurationFields;
